import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Image as ImageIcon, MapPin, Megaphone, Mic, Video } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useGetApiV1MissionsMissionIdChat,
  usePostApiV1MissionsMissionIdChatAnnounce,
} from '@/shared/api/generated/chat/chat';
import { problemDetail } from '@/shared/api/problem';
import { useIsMissionControl } from '@/features/missions/model/use-mission-control';
import { Button } from '@/shared/ui/Button';
import type { ChatMessageDto } from '@/shared/api/generated/model';
import { ChatMessageType } from '@/shared/api/generated/model';
import { useTopic } from '@/shared/lib/mqtt/use-topic';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Panel } from '@/shared/ui/Panel';
import { QueryError } from '@/shared/ui/QueryError';
import { EmptyState } from '@/shared/ui/EmptyState';
import { MediaAttachment } from '@/features/missions/ui/MediaAttachment';

/**
 * What the search party is saying to each other.
 *
 * ⚠️ **No composer for ordinary messages, and that is a decision rather than an omission.** §23.1
 * gives every control member the right to FOLLOW a mission inside its chat, and the backend still
 * refuses an ordinary write from the operations room. A composer for one would be a control that
 * fails on submit — the worst kind.
 *
 * ⚠️ **One thing the control room may write: an announcement** (decision of 22 August 2026). It is
 * drawn apart from where a message composer would sit, and offered to one person — the operator
 * holding THIS mission's open shift, which is exactly the test the server applies. Not every
 * control member: they may all read every mission's chat, and none of the others may speak into
 * this one.
 *
 * ⚠️ **Live over MQTT, seeded over HTTP.** The broker delivers what is said from now on and stores
 * nothing; the API holds the record (§23.9). A panel opened mid-search that subscribed only would
 * show an empty conversation and fill it one line at a time as people spoke — which reads exactly
 * like a search where nobody is talking.
 */
export function MissionChat({ missionId }: { missionId: string }) {
  const { t, locale } = useLocale();

  // ⚠️ The header has to agree with the body. Leaving the "follow only" chip up while an
  // announcement box sits underneath it tells the operator two different things about the same
  // panel, and the one they believe is the one that stops them using it.
  const iAmTheControl = useIsMissionControl(missionId);

  const history = useGetApiV1MissionsMissionIdChat(missionId);

  // ⚠️ Live arrivals are held here rather than written into the query cache. Merging into the cache
  // would make every message a cache write and a re-render of the page around this panel; a control
  // room leaves this open for a whole shift.
  const [live, setLive] = useState<ChatMessageDto[]>([]);

  useTopic(`mission/${missionId}/chat`, (payload) => {
    const message = payload as ChatMessageDto;
    if (message?.messageId == null) return;

    setLive((previous) =>
      previous.some((m) => m.messageId === message.messageId) ? previous : [...previous, message],
    );
  });

  // ⚠️ The live buffer is cleared when the history is refetched, not left to grow. Otherwise every
  // message that arrived live is also in the refetched page, and the deduplication below is doing
  // work that grows for the length of the shift.
  useEffect(() => {
    setLive([]);
  }, [history.dataUpdatedAt]);

  const messages = useMemo(() => {
    const seen = new Set((history.data ?? []).map((m) => m.messageId));
    return [...(history.data ?? []), ...live.filter((m) => !seen.has(m.messageId))];
  }, [history.data, live]);

  // ⚠️ Scrolls to the newest only when the newest changes — never on every render. A panel that
  // re-scrolls while an operator is reading upwards through the morning is a panel they stop using.
  const foot = useRef<HTMLDivElement>(null);
  const newest = messages.at(-1)?.messageId;
  useEffect(() => {
    foot.current?.scrollIntoView({ block: 'nearest' });
  }, [newest]);

  return (
    <Panel
      title={t.missions.chatHeading}
      description={iAmTheControl ? t.missions.chatControlCanAnnounce : t.missions.chatFollowOnly}
      actions={
        iAmTheControl ? null : (
          <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-theme-xs text-gray-500">
            <Eye aria-hidden className="size-3.5" />
            {t.missions.chatReadOnly}
          </span>
        )
      }
      bodyClassName="p-0"
    >
      {iAmTheControl ? (
        <AnnounceBox missionId={missionId} onSent={() => void history.refetch()} />
      ) : null}

      {history.isError ? (
        <div className="p-4 sm:p-6">
          <QueryError error={history.error} onRetry={() => history.refetch()} />
        </div>
      ) : history.isPending ? (
        <p className="p-4 text-theme-sm text-gray-500 sm:p-6">{t.app.loading}</p>
      ) : messages.length === 0 ? (
        <EmptyState
          icon={Eye}
          title={t.missions.chatEmpty}
          hint={t.missions.chatEmptyHint}
        />
      ) : (
        // ⚠️ A bounded, scrolling region rather than a list that grows the page. A long search
        // produces hundreds of messages, and a card that runs off the bottom of the screen pushes
        // the coverage figures and the member list out of reach.
        <div className="custom-scrollbar flex max-h-[28rem] flex-col gap-4 overflow-y-auto p-4 sm:p-6">
          {messages.map((message) => (
            <Message key={message.messageId} message={message} locale={locale} />
          ))}
          <div ref={foot} />
        </div>
      )}
    </Panel>
  );
}

/**
 * The one thing the control room may write into a mission's chat (decision of 22 August 2026).
 *
 * ⚠️ **Drawn only for the operator holding this mission's open shift** — the caller decides that,
 * with the same test the server applies. Every Desk Control operator passes the control policy and
 * may read every mission's chat; only one of them is answerable for this search.
 *
 * ⚠️ The consequence is stated above the field, because it is not the same as sending a message:
 * this reaches every member's handset as a push. Somebody who thinks they are typing into a chat
 * will use it for things that do not warrant waking a search party.
 */
function AnnounceBox({ missionId, onSent }: { missionId: string; onSent: () => void }) {
  const { t } = useLocale();

  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const announce = usePostApiV1MissionsMissionIdChatAnnounce({
    mutation: {
      onSuccess: () => {
        setBody('');
        setError(null);
        onSent();
      },
      onError: (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  return (
    <form
      className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();

        if (body.trim() !== '') {
          announce.mutate({ missionId, data: { body: body.trim() } });
        }
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="flex items-center gap-1.5 text-theme-sm font-semibold">
          <Megaphone aria-hidden className="size-4 text-warning" />
          {t.missions.announceHeading}
        </span>
        <span className="text-theme-xs text-gray-500">{t.missions.announceLede}</span>
        <textarea
          className="min-h-16 w-full rounded-lg border border-gray-200 bg-transparent p-3 text-theme-sm"
          value={body}
          placeholder={t.missions.announcePlaceholder}
          onChange={(event) => setBody(event.target.value)}
        />
      </label>

      {error ? (
        <span role="alert" className="text-theme-xs text-danger">
          {error}
        </span>
      ) : null}

      <div>
        <Button type="submit" disabled={announce.isPending || body.trim() === ''}>
          {t.missions.announceSend}
        </Button>
      </div>
    </form>
  );
}

const MEDIA_ICON: Partial<Record<string, LucideIcon>> = {
  [ChatMessageType.Voice]: Mic,
  [ChatMessageType.Image]: ImageIcon,
  [ChatMessageType.Video]: Video,
  [ChatMessageType.Location]: MapPin,
};

/**
 * One line of the conversation.
 *
 * ⚠️ **Nobody's message is on the right.** A chat where "mine" sits opposite "theirs" is built for a
 * participant, and the operations room is not one — every message here belongs to somebody else, so
 * a two-sided layout would be inventing a distinction that does not exist and stealing half the
 * width to do it.
 *
 * ⚠️ A system message is drawn as a rule across the conversation rather than as a bubble, because it
 * is not something a person said. §3 keeps them in the same table so history reads in order.
 */
function Message({ message, locale }: { message: ChatMessageDto; locale: string }) {
  const { t } = useLocale();

  // ⚠️ `-u-ca-gregory`, as every other date in this panel — and explicitly, never by default. A
  // bare `ar-SA` leaves the calendar to the browser's CLDR tables, which have moved: older ICU data
  // gave Saudi Arabia Umm al-Qura by default, and current data gives Gregorian (measured in Node's
  // ICU on 5 September 2026: «٢٢‏/٨‏/٢٠٢٦»). A date that depends on which browser reads it is the
  // wrong kind of date for a control room. The association's decision is Gregorian everywhere a
  // person reads one, and this tag is how that stops being a hope.
  const at = new Date(message.sentAt).toLocaleTimeString(`${locale}-u-ca-gregory`, {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (message.type === ChatMessageType.System) {
    // ⚠️ **Every system message is an announcement from the control room, because that is the only
    // thing that writes one.** The type was reserved for lines the server might write about itself,
    // the client refuses to send it, and until 22 August 2026 nothing produced one at all —
    // `AnnounceInMissionChatCommand` is now its single author.
    //
    // ⚠️ Drawn attributed rather than as an anonymous rule across the conversation. A rule is for
    // something nobody said; this was said by the person answerable for this search, and an
    // announcement from nobody is an order from nobody. If the server ever does write an
    // authorless system line, it needs a field that says so — a missing sender name would
    // otherwise silently turn a real announcement into one.
    return (
      <article className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
        <p className="flex flex-wrap items-baseline gap-2">
          <Megaphone aria-hidden className="size-4 shrink-0 text-warning" />
          <span className="text-theme-sm font-semibold">{t.missions.announceHeading}</span>
          <span className="text-theme-xs text-gray-500">{message.senderName}</span>
          <time dateTime={message.sentAt} className="text-theme-xs tabular-nums text-gray-500">
            {at}
          </time>
        </p>

        <p className="whitespace-pre-wrap break-words text-theme-sm">{message.body}</p>
      </article>
    );
  }

  const Icon = MEDIA_ICON[message.type];

  return (
    <article className="flex flex-col gap-1">
      <p className="flex flex-wrap items-baseline gap-2">
        <span className="text-theme-sm font-semibold text-gray-800">{message.senderName}</span>

        {/* ⚠️ Read as it stands NOW, not as it stood when the line was typed — the server projects
            it that way on purpose. Somebody scrolling needs to know who to follow, and a badge that
            vanished halfway up the history would read as two different leaders. */}
        {message.senderIsLeader ? (
          <span className="rounded-full bg-brand-500/10 px-2 py-0.5 text-theme-xs text-brand-500">
            {t.missions.detailLeader}
          </span>
        ) : null}
        <time
          dateTime={message.sentAt}
          className="text-theme-xs tabular-nums text-gray-500"
          title={new Date(message.sentAt).toLocaleString(`${locale}-u-ca-gregory`)}
        >
          {at}
        </time>
      </p>

      <div className="rounded-md rounded-ss-none border border-gray-200 bg-gray-50 px-3 py-2">
        {Icon && message.mediaId ? (
          <MediaAttachment
            mediaId={message.mediaId}
            kind={message.type}
            icon={Icon}
            caption={message.body}
          />
        ) : message.type === ChatMessageType.Location ? (
          <Coordinates body={message.body} />
        ) : (
          // ⚠️ `whitespace-pre-wrap`: somebody typing coordinates or a list of landmarks puts them
          // on separate lines, and collapsing that is losing what they meant.
          <p className="whitespace-pre-wrap break-words text-theme-sm text-gray-700">
            {message.body}
          </p>
        )}
      </div>

      <span className="sr-only">{t.missions.chatSentBy.replace('{name}', message.senderName)}</span>
    </article>
  );
}

/**
 * A point somebody deliberately sent — "I am here, look".
 *
 * ⚠️ **Not the raw payload.** The handset sends `{"lat":29.9712,"lon":40.2038}` and the first
 * version printed exactly that, braces and quotes and all, into a control room. A coordinate is a
 * number an operator reads aloud over a radio; JSON punctuation is noise they have to parse past
 * while somebody is waiting on the other end.
 *
 * ⚠️ Six decimal places, which is about 11 cm — the precision a phone actually has. Printing what
 * the float happens to carry suggests an accuracy the GPS never claimed.
 *
 * ⚠️ Falls back to the raw body rather than hiding it. A payload this fails to parse is still
 * something a member sent, and an operator who can see it can read it out; an empty bubble is a
 * message that vanished.
 */
function Coordinates({ body }: { body?: string | null }) {
  const { t } = useLocale();

  const point = ((): { lat: number; lon: number } | null => {
    try {
      const parsed = JSON.parse(body ?? '') as { lat?: unknown; lon?: unknown };
      return typeof parsed.lat === 'number' && typeof parsed.lon === 'number'
        ? { lat: parsed.lat, lon: parsed.lon }
        : null;
    } catch {
      return null;
    }
  })();

  return (
    <p className="flex items-center gap-2 text-theme-sm text-gray-700">
      <MapPin aria-hidden className="size-4 shrink-0 text-brand-500" />

      {point ? (
        <>
          <span className="sr-only">{t.missions.chatLocation}</span>
          {/* ⚠️ `dir="ltr"` and tabular figures: a decimal coordinate is a left-to-right number, and
              in an Arabic paragraph it mirrors into something that reads as a different place. */}
          <span dir="ltr" className="font-mono text-theme-xs tabular-nums">
            {point.lat.toFixed(6)}, {point.lon.toFixed(6)}
          </span>
        </>
      ) : (
        <span dir="ltr" className="font-mono text-theme-xs break-all">
          {body}
        </span>
      )}
    </p>
  );
}
